import { ref } from 'vue'
import { getAuth } from 'firebase/auth'
import { collection, getDocs, query, updateDoc, doc, where } from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { isInternalEmail } from '@/utils/emailDomains'
import {
  clientTypeForRole,
  resolveUserRole,
  type CompanyPermissions,
  type CompanyRole,
  type ModulePermissions,
  type UserRole
} from '@/domain/types/Role'

export interface CompanyUser {
  uid: string
  name: string
  lastName: string
  email: string
  role: UserRole
  status: string
  lastLoginAt: Date | null
  /** Permisos del usuario en TODAS sus compañías; se conservan al guardar. */
  allPermissions: CompanyPermissions
  /** Permisos en la compañía activa. undefined = sin configurar, acceso completo. */
  permissions: ModulePermissions | undefined
}

const toDate = (value: any): Date | null => {
  if (!value) return null
  if (typeof value.toDate === 'function') return value.toDate()
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Los usuarios antiguos tenían permissions como array; se ignora. */
const asPermissionsMap = (value: any): CompanyPermissions =>
  value && !Array.isArray(value) && typeof value === 'object' ? value : {}

export function useCompanyUsers() {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()
  const db = FirebaseProvider.getInstance().getFirestore()

  const users = ref<CompanyUser[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const currentCompanyId = () => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) throw new Error('No hay compañía seleccionada')
    return companyId
  }

  const fetchUsers = async () => {
    try {
      isLoading.value = true
      error.value = null

      const companyId = currentCompanyId()
      const snapshot = await getDocs(
        query(collection(db, 'Users'), where('companyIds', 'array-contains', companyId))
      )

      // El equipo de la plataforma no aparece en el listado de una compañía cliente;
      // solo se ven entre ellos.
      const viewerIsInternal = isInternalEmail(authStore.user?.email || '')

      users.value = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data()
          const allPermissions = asPermissionsMap(data.permissions)

          return {
            uid: docSnap.id,
            name: data.name || '',
            lastName: data.lastName || '',
            email: data.email || '',
            role: resolveUserRole(data.role, data.clientType) || 'regular',
            status: data.status || 'active',
            lastLoginAt: toDate(data.lastLoginAt),
            allPermissions,
            permissions: allPermissions[companyId]
          }
        })
        .filter((user) => viewerIsInternal || !isInternalEmail(user.email))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
    } catch (err: any) {
      error.value = err.message || 'Error cargando los usuarios'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const adminCount = () => users.value.filter((u) => u.role === 'admin').length

  /** Nadie puede cambiarse el rol a sí mismo ni dejar la compañía sin admins. */
  const assertRoleChangeAllowed = (user: CompanyUser, role: CompanyRole) => {
    if (user.uid === authStore.user?.uid) {
      throw new Error('No puedes cambiar tu propio rol')
    }
    if (user.role === 'admin' && role !== 'admin' && adminCount() <= 1) {
      throw new Error('Es el único administrador de la compañía. Asigna otro antes de cambiarle el rol.')
    }
  }

  const changeRole = async (user: CompanyUser, role: CompanyRole) => {
    assertRoleChangeAllowed(user, role)

    await updateDoc(doc(db, 'Users', user.uid), {
      role,
      clientType: clientTypeForRole(role),
      updatedAt: new Date()
    })

    user.role = role
  }

  /**
   * Guarda los permisos del usuario en la compañía activa. Si conserva todas las
   * acciones de todos los módulos disponibles, se borra el mapa: la ausencia
   * significa acceso completo, y así los módulos que se habiliten más adelante
   * quedan accesibles sin tener que reeditar a cada usuario.
   */
  const savePermissions = async (
    user: CompanyUser,
    permissions: ModulePermissions,
    hasEverything: boolean
  ) => {
    const companyId = currentCompanyId()
    const allPermissions = { ...user.allPermissions }

    if (hasEverything) delete allPermissions[companyId]
    else allPermissions[companyId] = permissions

    await updateDoc(doc(db, 'Users', user.uid), {
      permissions: allPermissions,
      updatedAt: new Date()
    })

    user.allPermissions = allPermissions
    user.permissions = allPermissions[companyId]
  }

  /**
   * Elimina al usuario a través de la Cloud Function: si pertenece a otras
   * compañías solo se desvincula de esta, y si era la última se borra también su
   * cuenta de Firebase Auth. Borrarlo desde el cliente no es posible.
   */
  const removeUser = async (user: CompanyUser) => {
    if (user.uid === authStore.user?.uid) {
      throw new Error('No puedes eliminarte a ti mismo')
    }
    if (user.role === 'admin' && adminCount() <= 1) {
      throw new Error('Es el único administrador de la compañía. Asigna otro antes de eliminarlo.')
    }

    const currentUser = getAuth().currentUser
    if (!currentUser) throw new Error('Sesión no válida')

    const token = await currentUser.getIdToken()
    const response = await fetch(`${import.meta.env.VITE_CF_BASE_URL}/DELETE_COMPANY_USER`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ company: currentCompanyId(), uid: user.uid })
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'No se pudo eliminar el usuario')
    }

    users.value = users.value.filter((u) => u.uid !== user.uid)
    return result.action as 'deleted' | 'unlinked'
  }

  return {
    users,
    isLoading,
    error,
    fetchUsers,
    changeRole,
    savePermissions,
    removeUser
  }
}
